import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export default function QuickView({ product, onClose }) {
  const { add } = useCart();
  const [qty, setQty] = useState(1);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-forest/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="quick-view-overlay"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="quick-view-modal"
            className="relative w-full max-w-2xl overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl"
          >
            <button
              data-testid="quick-view-close"
              onClick={onClose}
              aria-label="Close quick view"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-forest shadow-md"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="grid sm:grid-cols-2">
              <div className="relative aspect-square">
                <div className="absolute inset-0" style={{ background: `radial-gradient(80% 70% at 50% 35%, ${product.accent}30 0%, transparent 70%)` }} />
                <img src={product.image} alt={product.name} className="absolute inset-0 h-full w-full object-cover p-6" />
              </div>
              <div className="flex flex-col gap-3 p-6 sm:p-8">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: product.accent }}>
                  {product.mascot} · {product.tagline}
                </p>
                <h3 className="font-display text-2xl font-extrabold text-forest">{product.name}</h3>
                <p className="text-sm leading-relaxed text-ink/70">{product.benefit} {product.description.split(".")[0]}.</p>
                <p className="font-display text-2xl font-extrabold text-forest">
                  {inr(product.price)} <span className="font-mono text-xs font-medium text-ink/40">/ {product.pack}</span>
                </p>
                <div className="mt-auto flex items-center gap-3 pt-2">
                  <div className="flex items-center rounded-full border border-sand">
                    <button data-testid="quick-view-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-forest"><Minus className="h-4 w-4" /></button>
                    <span data-testid="quick-view-qty" className="w-8 text-center font-mono text-sm font-bold">{qty}</span>
                    <button data-testid="quick-view-qty-plus" onClick={() => setQty(qty + 1)} className="px-3 py-2 text-forest"><Plus className="h-4 w-4" /></button>
                  </div>
                  <button
                    data-testid="quick-view-add-to-cart"
                    onClick={() => { add(product, qty); onClose(); }}
                    className="flex-1 rounded-full bg-leaf py-3 text-sm font-extrabold text-white transition-colors hover:bg-forest active:scale-95"
                  >
                    Add to cart
                  </button>
                </div>
                <Link to={`/product/${product.slug}`} data-testid="quick-view-full-link" className="flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-wider text-forest/70 hover:text-leaf">
                  Full story <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
